/**
 * Rule builder model: pure parse / serialize / format for the four row
 * types plus an advanced free-text fallback.
 *
 * The server stores and evaluates arbitrary expressions in the grammar
 * described in services/expression/*; this module is a UI-only
 * abstraction that recognises the common shapes used in practice
 * (ranges, single comparisons, parity/divisibility) and leaves anything
 * else as an opaque advanced expression. Serialization always produces
 * valid server-side syntax.
 *
 * Round-trip guarantee:
 *   parse(serialize(row))   ≡ row (modulo irrelevant whitespace)
 *   serialize(parse(expr))  ≡ expr whenever expr matches a known shape;
 *                            otherwise parse(expr) is {kind:'advanced',
 *                            expression: expr.trim()} and serializes to
 *                            that same trimmed string.
 */
const IDENT = '[a-zA-Z_][a-zA-Z_0-9]*';
const NUMBER = '\\d+(?:\\.\\d+)?';
const RHS = `(?:${IDENT}|${NUMBER})`;
const SP = '\\s*';
function reExact(body) {
    return new RegExp(`^${SP}${body}${SP}$`);
}
/** Parse a server-side rule expression into a builder row. Unknown
 *  shapes fall back to {kind: 'advanced'} with the trimmed source. */
export function parseExpression(raw) {
    const expr = raw.trim();
    // Range: `x >= N && x <= M`
    const rangeA = reExact(`(${IDENT})${SP}>=${SP}(${NUMBER})${SP}&&${SP}(${IDENT})${SP}<=${SP}(${NUMBER})`).exec(expr);
    if (rangeA && rangeA[1] === rangeA[3]) {
        return { kind: 'range', variable: rangeA[1], min: rangeA[2], max: rangeA[4] };
    }
    // Range reversed: `x <= M && x >= N`
    const rangeB = reExact(`(${IDENT})${SP}<=${SP}(${NUMBER})${SP}&&${SP}(${IDENT})${SP}>=${SP}(${NUMBER})`).exec(expr);
    if (rangeB && rangeB[1] === rangeB[3]) {
        return { kind: 'range', variable: rangeB[1], min: rangeB[4], max: rangeB[2] };
    }
    // Divisibility — strictly matched BEFORE generic comparison so `x % 2 == 0`
    // doesn't get consumed as a comparison with an expression on the left.
    const divEven = reExact(`(${IDENT})${SP}%${SP}2${SP}==${SP}0`).exec(expr);
    if (divEven)
        return { kind: 'divisibility', variable: divEven[1], mode: 'even' };
    const divOdd = reExact(`(${IDENT})${SP}%${SP}2${SP}!=${SP}0`).exec(expr);
    if (divOdd)
        return { kind: 'divisibility', variable: divOdd[1], mode: 'odd' };
    const divBy = reExact(`(${IDENT})${SP}%${SP}(${RHS})${SP}==${SP}0`).exec(expr);
    if (divBy) {
        const d = divBy[2];
        const isNum = /^\d/.test(d);
        return {
            kind: 'divisibility',
            variable: divBy[1],
            mode: 'divisibleBy',
            divisorKind: isNum ? 'number' : 'variable',
            divisorValue: d,
        };
    }
    // Single comparison: `x OP (number|identifier)`. 2-char operators
    // listed before 1-char ones so the regex alternation prefers them.
    const comp = reExact(`(${IDENT})${SP}(>=|<=|==|!=|>|<)${SP}(${RHS})`).exec(expr);
    if (comp) {
        const rhs = comp[3];
        const isNum = /^\d/.test(rhs);
        return {
            kind: 'comparison',
            variable: comp[1],
            op: comp[2],
            rhsKind: isNum ? 'number' : 'variable',
            rhsValue: rhs,
        };
    }
    return { kind: 'advanced', expression: expr };
}
/** Serialize a builder row back to a server-side expression string.
 *  Produces canonical spacing: single space around every operator. */
export function serializeRow(row) {
    switch (row.kind) {
        case 'range':
            return `${row.variable} >= ${row.min} && ${row.variable} <= ${row.max}`;
        case 'comparison':
            return `${row.variable} ${row.op} ${row.rhsValue}`;
        case 'divisibility':
            if (row.mode === 'even')
                return `${row.variable} % 2 == 0`;
            if (row.mode === 'odd')
                return `${row.variable} % 2 != 0`;
            return `${row.variable} % ${row.divisorValue ?? ''} == 0`;
        case 'advanced':
            return row.expression;
    }
}
/** Default row inserted when the operator presses "הוסף חוק". */
export function emptyRow(variables) {
    return { kind: 'range', variable: variables[0] ?? 'a', min: '', max: '' };
}
/** Variables referenced inside the row (used to inject options into the
 *  variable dropdowns so parsed-but-unknown names still appear). */
export function variablesInRow(row) {
    switch (row.kind) {
        case 'range':
            return [row.variable];
        case 'comparison':
            return row.rhsKind === 'variable'
                ? [row.variable, row.rhsValue]
                : [row.variable];
        case 'divisibility':
            return row.divisorKind === 'variable' && row.divisorValue
                ? [row.variable, row.divisorValue]
                : [row.variable];
        case 'advanced':
            return [];
    }
}
const HE_OP = {
    '>': 'גדול מ-',
    '>=': 'גדול או שווה ל-',
    '==': 'שווה ל-',
    '!=': 'שונה מ-',
    '<': 'קטן מ-',
    '<=': 'קטן או שווה ל-',
};
/** Human-readable Hebrew phrasing for the rules list. Returns null for
 *  advanced rows (caller falls back to raw text). */
export function formatHebrew(row) {
    switch (row.kind) {
        case 'range':
            return `${row.variable} בין ${row.min} ל-${row.max}`;
        case 'comparison':
            return `${row.variable} ${HE_OP[row.op]}${row.rhsValue}`;
        case 'divisibility':
            if (row.mode === 'even')
                return `${row.variable} זוגי`;
            if (row.mode === 'odd')
                return `${row.variable} אי-זוגי`;
            return `${row.variable} מתחלק ב-${row.divisorValue ?? ''}`;
        case 'advanced':
            return null;
    }
}
/** Derive the variable list for the dropdowns from a template's prompt
 *  and answer rule. Matches ParamsFromPrompt (TemplateModals.tsx): any
 *  identifier inside `{braces}` in the prompt, plus any non-function
 *  identifier in the answer rule. */
const EXPR_FNS = new Set(['abs', 'floor', 'ceil', 'round', 'sqrt', 'min', 'max']);
export function variablesFromTemplate(prompt, answerRule) {
    const set = new Set();
    for (const m of prompt.matchAll(/\{([a-zA-Z_][a-zA-Z_0-9]*)\}/g))
        set.add(m[1]);
    for (const m of answerRule.matchAll(/[a-zA-Z_][a-zA-Z_0-9]*/g)) {
        if (!EXPR_FNS.has(m[0]))
            set.add(m[0]);
    }
    return [...set];
}
/** Local client-side validation for a row. Returns null when valid,
 *  else a Hebrew message suitable for inline display. The server still
 *  validates on save; this just catches obvious mistakes early. */
export function validateRow(row) {
    switch (row.kind) {
        case 'range': {
            if (!row.variable)
                return 'יש לבחור משתנה';
            if (row.min === '' || row.max === '')
                return 'יש להזין טווח מלא';
            const lo = Number(row.min), hi = Number(row.max);
            if (!Number.isFinite(lo) || !Number.isFinite(hi))
                return 'ערכי הטווח חייבים להיות מספרים';
            if (lo > hi)
                return 'הערך המינימלי גדול מהערך המקסימלי';
            return null;
        }
        case 'comparison': {
            if (!row.variable)
                return 'יש לבחור משתנה';
            if (!row.rhsValue)
                return 'יש להזין ערך';
            if (row.rhsKind === 'number' && !Number.isFinite(Number(row.rhsValue))) {
                return 'הערך חייב להיות מספר';
            }
            return null;
        }
        case 'divisibility': {
            if (!row.variable)
                return 'יש לבחור משתנה';
            if (row.mode === 'divisibleBy') {
                if (!row.divisorValue)
                    return 'יש להזין מחלק';
                if (row.divisorKind === 'number' && !Number.isFinite(Number(row.divisorValue))) {
                    return 'המחלק חייב להיות מספר';
                }
            }
            return null;
        }
        case 'advanced': {
            if (!row.expression.trim())
                return 'יש להזין ביטוי';
            return null;
        }
    }
}
